import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { useDeleteFacility } from '~/api/facility';
import type { Facility } from './OrganizationFacilities';
import { Button } from '~/components/ui';
import { extractGraphQLErrorMessages } from '~/utils';
import { facilityDisplayLabel } from './facilityLabel';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  facility: Facility;
  onDeleted: (id: string) => void;
};

export function DeleteFacilityDialog({
  isOpen,
  onClose,
  facility,
  onDeleted,
}: Props) {
  const { t } = useTranslation('settings');
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const deleteFacility = useDeleteFacility();

  function handleClose() {
    setErrorMessage(null);
    setIsDeleting(false);
    onClose();
  }

  async function handleConfirm() {
    setIsDeleting(true);
    setErrorMessage(null);
    try {
      await deleteFacility.mutateAsync({ id: facility.id });
      onDeleted(facility.id);
      toast.success(t('settings.facilities.delete_success'));
      handleClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? extractGraphQLErrorMessages(err).join(', ')
          : t('settings.facilities.delete_error');
      setErrorMessage(message);
      setIsDeleting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-neut-bg-alt rounded-lg p-6 w-full max-w-sm">
        <h2
          id="Modal-title"
          className="text-base font-semibold text-neut-prim mb-2"
        >
          {t('settings.facilities.delete_dialog.title', {
            code: facilityDisplayLabel(facility),
          })}
        </h2>
        <p className="text-sm text-neut-sec mb-4">
          {t('settings.facilities.delete_dialog.message')}
        </p>

        {errorMessage && (
          <p className="text-sm text-error-500 mb-4">{errorMessage}</p>
        )}

        <div className="flex gap-3">
          <Button
            size="sm"
            onClick={handleClose}
            disabled={isDeleting}
            variant="accent"
          >
            {t('settings.facilities.delete_dialog.cancel')}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {t('settings.facilities.delete_dialog.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
